import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { Badge } from './badge';
import { X } from 'lucide-react';
import { SelectableEntity } from '../../lib/interfaces/selectable-entity.interface';

interface UserSelectProps {
  value: SelectableEntity | SelectableEntity[] | null;
  onChange: (value: any) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const UserSelect: React.FC<UserSelectProps> = ({
  value,
  onChange,
  multiple = false,
  placeholder = 'Benutzer auswählen...',
  disabled = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [users, setUsers] = useState<SelectableEntity[]>([]);
  const [loading, setLoading] = useState(false);

  // TEMPORARY DUMMY DATA - Replace with API call when fixed
  const dummyUsers: SelectableEntity[] = [
    { _id: '1', value: 'Andreas Osterrieder' },
    { _id: '2', value: 'Andreas Poschmann' },
    { _id: '3', value: 'Christian Kaiser' },
    { _id: '4', value: 'Clemens Karge' },
    { _id: '5', value: 'Dirk Bienert' },
    { _id: '6', value: 'Felix Endres' },
    { _id: '7', value: 'Freddy Blum' },
    { _id: '8', value: 'Helmut Fenzl' },
    { _id: '9', value: 'Jürgen Schlosser' },
    { _id: '10', value: 'Kai Bockelberg' },
    { _id: '11', value: 'Michael Lerf' },
    { _id: '12', value: 'Peter Münstermann' },
    { _id: '13', value: 'Ramon Alvarez' },
    { _id: '14', value: 'Raphaela Sainovic' },
    { _id: '15', value: 'Tobias Bierek' },
    { _id: '16', value: 'Julian Heißwolf' },
    { _id: '17', value: 'Andi Hysa' },
    { _id: '18', value: 'Andi Seller Test' },
  ];

  useEffect(() => {
    // Set dummy data immediately
    setUsers(dummyUsers);
    
    // UNCOMMENT BELOW WHEN API IS FIXED
    /*
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_API}/v1.0/settings/users/active-users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Users API Response:', data); // Debug log
          // Handle both array and object response
          if (Array.isArray(data)) {
            setUsers(data);
          } else if (data && data.data) {
            setUsers(data.data);
          } else if (data && data.docs) {
            setUsers(data.docs);
          } else {
            console.error('Unexpected user data format:', data);
            setUsers([]);
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
    */
  }, [getAccessTokenSilently]);

  if (multiple) {
    const selectedUsers = (value as SelectableEntity[]) || [];
    const selectedIds = selectedUsers.map(u => u._id);

    const handleSelect = (userId: string) => {
      const user = users.find(u => u._id === userId);
      if (user && !selectedIds.includes(userId)) {
        onChange([...selectedUsers, user]);
      }
    };

    const handleRemove = (userId: string) => {
      onChange(selectedUsers.filter(u => u._id !== userId));
    };

    return (
      <div className="space-y-2">
        <Select onValueChange={handleSelect} disabled={disabled || loading}>
          <SelectTrigger>
            <SelectValue placeholder={loading ? 'Lädt...' : placeholder} />
          </SelectTrigger>
          <SelectContent>
            {users
              .filter(u => !selectedIds.includes(u._id))
              .map(user => (
                <SelectItem key={user._id} value={user._id}>
                  {user.value}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map(user => (
              <Badge key={user._id} variant="secondary" className="pr-1">
                {user.value}
                <button
                  type="button"
                  onClick={() => handleRemove(user._id)}
                  disabled={disabled}
                  className="ml-1 rounded-full p-0.5 hover:bg-gray-300 disabled:opacity-50"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Single select
  const selectedUser = value as SelectableEntity | null;

  return (
    <Select
      value={selectedUser?._id}
      onValueChange={(userId) => {
        const user = users.find(u => u._id === userId);
        onChange(user || null);
      }}
      disabled={disabled || loading}
    >
      <SelectTrigger>
        <SelectValue placeholder={loading ? 'Lädt...' : placeholder}>
          {selectedUser?.value}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {users.map(user => (
          <SelectItem key={user._id} value={user._id}>
            {user.value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
